// Jumeau JS de `Plans::LotColor` (app/services/plans/lot_color.rb), refonte planning PR 2, Task 8 (K-07, N-4,
// RV-41). Il ne sert QU'À l'aperçu du sélecteur de couleur (`color_picker_controller.js`), seule exception bornée
// où le JS pose une couleur : le Gantt, lui, reçoit ses couleurs de l'îlot `<style>` serveur et ne pose que des
// noms de classe (piège n° 3).
//
// Toute modification de `normalize` ou de `ink` se fait DES DEUX CÔTÉS, tenue par la table partagée
// `test/fixtures/files/lot_color_contrast_vectors.json` : `lot_color_test.rb` la vérifie côté Ruby,
// `lot_color_js_parity_test.rb` lance `test/javascript/support/lot_color_vectors_runner.mjs` et compare chaque
// sortie JS à la sortie Ruby (jamais une table attendue recopiée). Les 7 couleurs sans encre AA sous le voile
// (RV-41) sont listées dans `Plans::LotColor::VEIL_EXCEPTIONS`.
//
// Hors de tout contrôleur pour tourner sous `node --test` (aucun DOM).

const FORMAT = /^#[0-9a-f]{6}$/;

// Encres candidates (D18, N-4), dans l'ordre de préférence : blanc, neutral-900, noir pur.
const INKS = [
  ['white', '#ffffff'],
  ['dark', '#0f172b'],
  ['black', '#000000'],
];
const AA = 4.5;
// Le voile blanc de 25 % des barres `gantt-bar-gloss` / `gantt-bar-stripes` (RV-41).
const VEIL_ALPHA = 0.25;

/**
 * `' #E8B04B '` → `'#e8b04b'` ; vide ou nul → `null` (couleur par défaut, D9). Une chaîne invalide reste
 * invalide, jamais « réparée » (ni dièse ajouté, ni forme courte étendue) : `isValid` tranche.
 * @param {unknown} raw
 * @returns {string|null}
 */
export function normalize(raw) {
  if (raw === null || raw === undefined) return null;
  const value = String(raw).trim().toLowerCase();
  return value === '' ? null : value;
}

/**
 * @param {unknown} hex
 * @returns {boolean} vrai pour `#rrggbb` minuscule, rien d'autre
 */
export function isValid(hex) {
  return typeof hex === 'string' && FORMAT.test(hex);
}

function channels(hex) {
  return [1, 3, 5].map((at) => parseInt(hex.slice(at, at + 2), 16));
}

function linearize(value) {
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

// WCAG 2.x : linéarisation sRGB (seuil 0,04045), puis L = 0,2126 R + 0,7152 G + 0,0722 B.
function luminanceOf(rgb) {
  const [red, green, blue] = rgb.map((channel) => linearize(channel / 255));
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function ratio(a, b) {
  const high = Math.max(a, b);
  const low = Math.min(a, b);
  return (high + 0.05) / (low + 0.05);
}

const INK_LUMINANCE = Object.fromEntries(
  INKS.map(([key, hex]) => [key, luminanceOf(channels(hex))])
);

function plainInk(hex) {
  const luminance = luminanceOf(channels(hex));
  const white = ratio(luminance, INK_LUMINANCE.white);
  const dark = ratio(luminance, INK_LUMINANCE.dark);
  const [best, bestRatio] = white >= dark ? ['white', white] : ['dark', dark];
  return bestRatio >= AA ? best : 'black';
}

function veiledInk(hex) {
  const rgb = channels(hex);
  const pure = luminanceOf(rgb);
  const composite = luminanceOf(rgb.map((c) => (1 - VEIL_ALPHA) * c + VEIL_ALPHA * 255));
  const worst = INKS.map(([key]) => [
    key,
    Math.min(ratio(pure, INK_LUMINANCE[key]), ratio(composite, INK_LUMINANCE[key])),
  ]);
  const passing = worst.find(([, value]) => value >= AA);
  if (passing) return passing[0];
  return worst.reduce((best, entry) => (entry[1] > best[1] ? entry : best))[0];
}

/**
 * L'encre lisible sur une couleur de lot (N-4) : `'white'`, `'dark'` (neutral-900) ou `'black'`.
 * `veil: true` (RV-41) : sur le composite pire cas sous le voile blanc de 25 % ; si aucune encre ne tient
 * 4,5:1 sur la couleur ET le composite, celle qui maximise le pire des deux.
 * @param {string} hex `#rrggbb` valide
 * @param {{ veil?: boolean }} [options]
 * @returns {'white'|'dark'|'black'}
 */
export function ink(hex, { veil = false } = {}) {
  return veil ? veiledInk(hex) : plainInk(hex);
}
